/**
 * API client and shared types for the dashboard.
 *
 * Every request sends the session cookie and treats a 401 as "session gone",
 * which the app turns into a return to the login screen rather than a broken
 * page full of empty tables.
 */

export interface Opportunity {
  id: string;
  symbol: string;
  buyExchange: string;
  sellExchange: string;
  buyPrice: number;
  sellPrice: number;
  buyQtyAtTop: number;
  sellQtyAtTop: number;
  grossPct: number;
  netPct: number;
  netProfitQuote: number;
  sizeQuote: number;
  detectedAt: number;
  expiresAt: number;
  mode?: ArbitrageMode;
  /**
   * False for a row above the watch level but below the trade bar: real
   * detection, not something to act on. Absent on older payloads, so treat
   * undefined as tradeable.
   */
  tradeable?: boolean;
  /**
   * Why this row cannot be traded, when the reason is not simply that the gap
   * is too small — currently a missing deposit address on the destination.
   */
  blockedReason?: string;
  /**
   * The row stays listed until this moment even if the gap has gone. It is NOT
   * a licence to execute — `expiresAt` still governs that, and a held row whose
   * quote has gone stale is visible and unclickable.
   */
  holdUntil?: number;
  transfer?: TransferDetail;
}

/**
 * Why a venue can or cannot take part in a transfer. A venue with no fee data
 * and one that genuinely cannot send look identical from outside — both just
 * never produce a tradeable route — so they are named separately here.
 */
export interface VenueRouting {
  exchange: string;
  label: string;
  /** null when the venue offers no way to ask — not the same as false. */
  keyCanWithdraw: boolean | null;
  sendableCoins: number;
  receivableCoins: number;
  canSend: boolean;
  canReceive: boolean;
  blocker: string | null;
}

export interface FeedStatus {
  exchange: string;
  state: 'idle' | 'connecting' | 'live' | 'reconnecting' | 'error' | 'stopped';
  lastUpdateAt: number | null;
  lastError: string | null;
  reconnects: number;
  subscribedSymbols: number;
  staleSymbols: number;
  updatedAt: number;
}

export interface Settings {
  thresholdPct: number;
  bufferPct: number;
  defaultSizeQuote: number;
  maxTradeSizeQuote: number;
  stalenessMs: number;
  alertsEnabled: boolean;
  telegramEnabled: boolean;
  emailEnabled: boolean;
  alertCooldownMs: number;
  alertMinNetPct: number;
}

export interface AssetBalance {
  asset: string;
  free: number;
  used: number;
  total: number;
}

export interface VenueBalances {
  exchange: string;
  label: string;
  configured: boolean;
  ok: boolean;
  error: string | null;
  balances: AssetBalance[];
  fetchedAt: number;
}

export interface ExchangeKeyStatus {
  exchange: string;
  label: string;
  enabled: boolean;
  configured: boolean;
  apiKeyMasked: string | null;
  requiresPassword: boolean;
  hasPassword: boolean;
  verifiedAt: string | null;
  takerFee: number | null;
}

export interface Trade {
  id: string;
  symbol: string;
  buyExchange: string;
  sellExchange: string;
  status: 'PENDING' | 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'REJECTED';
  sizeQuote: string;
  buyFillPrice: string | null;
  sellFillPrice: string | null;
  netPnl: string | null;
  failureReason: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface OpportunityEpisode {
  id: string;
  symbol: string;
  buyExchange: string;
  sellExchange: string;
  netPct: number;
  peakNetPct: number;
  grossPct: number;
  netProfitQuote: string;
  detectedAt: string;
  expiredAt: string | null;
  alertedAt: string | null;
}


// ------------------------------------------------------------------ multi-mode

export type ArbitrageMode = 'transfer' | 'prepositioned';

export interface TransferDetail {
  coin: string;
  network: string;
  withdrawalFeeCoin: number;
  withdrawalDragPct: number;
  baseArrived: number;
  estimatedMinutes: number;
  minimumViableSize: number;
  feeExceedsGap: boolean;
}

export interface ModeInfo {
  label: string;
  description: string;
  capitalModel: string;
  risk: string;
}

export interface ModeState {
  mode: ArbitrageMode;
  executionEnabled: boolean;
  pocMeasurement: boolean;
  info: ModeInfo;
  requiredPermissions: string[];
  keyGuidance: string;
  availableModes: Array<{ mode: ArbitrageMode } & ModeInfo>;
  transfer: {
    minNetPct: number;
    /** Rows are surfaced from here up; 0 means only tradeable ones appear. */
    watchPct: number;
    /** Seconds a row is kept on screen after it opens. 0 disables the hold. */
    minHoldSeconds: number;
    expectedMinutes: number;
    timeoutMinutes: number;
  } | null;
}

export interface HorizonStats {
  horizonMinutes: number;
  measured: number;
  missing: number;
  stillProfitable: number;
  stillBreakeven: number;
  survivalRate: number;
  breakevenRate: number;
  meanNetPct: number;
  medianNetPct: number;
  p25NetPct: number;
  p75NetPct: number;
  medianExecutableQuote: number | null;
}

export interface PocReport {
  qualifyingEpisodes: number;
  totalEpisodes: number;
  reportThresholdPct: number;
  windowStart: number | null;
  windowEnd: number | null;
  horizons: HorizonStats[];
  byCoin: Array<{
    coin: string;
    episodes: number;
    qualifyingEpisodes: number;
    medianNetPctT0: number;
    bestNetPctT0: number;
    finalSurvivalRate: number | null;
    withdrawalFeeCoin: number;
  }>;
  medianExecutableQuoteT0: number | null;
  verdict: { decision: 'go' | 'no-go' | 'inconclusive'; headline: string; reasoning: string[] };
}

export interface PocProgress {
  enabled: boolean;
  startThresholdPct: number;
  observeThresholdPct: number;
  horizonsMinutes: number[];
  progress: {
    total: number;
    qualifying: number;
    closed: number;
    active: number;
    firstAt: string | null;
    lastAt: string | null;
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/** Thrown on 401 so the app can drop back to the login screen. */
export class UnauthenticatedError extends ApiError {}

/**
 * Not every response comes from the API.
 *
 * While the api container restarts, nginx answers with its own HTML error
 * page. Parsing that as JSON used to throw a raw SyntaxError — "Unexpected
 * token '<'" — which every polling screen then displayed as if the data were
 * malformed. The server being briefly down is an ordinary event during a
 * deploy, and it should read as one.
 */
function unreachable(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return `the server is not responding (${status}) — it is probably restarting, this usually clears in a few seconds`;
  }
  return `the server returned an unexpected response (${status})`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    // No response at all: the browser is offline, or nothing is listening.
    throw new ApiError('cannot reach the server — check the connection, or wait if it is restarting', 0);
  }

  const text = await response.text();
  let body: Record<string, unknown> = {};
  let isJson = true;
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      isJson = false;
    }
  }

  if (response.status === 401) {
    throw new UnauthenticatedError(String(body.error ?? 'not authenticated'), 401, body);
  }
  if (!response.ok) {
    throw new ApiError(
      isJson ? String(body.error ?? `request failed (${response.status})`) : unreachable(response.status),
      response.status,
      body,
    );
  }
  // A 200 that is not JSON means something answered in the API's place.
  if (!isJson) throw new ApiError(unreachable(response.status), response.status);

  return body as T;
}

export const api = {
  authStatus: () => request<{ setupRequired: boolean }>('/api/auth/status'),

  login: (username: string, password: string, totpCode?: string) =>
    request<{ username: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, totpCode }),
    }),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<{ username: string; totpEnabled: boolean; lastLoginAt: string | null }>('/api/auth/me'),

  beginTotp: () => request<{ secret: string; provisioningUri: string }>('/api/auth/totp/begin', { method: 'POST' }),

  enableTotp: (code: string) =>
    request<{ totpEnabled: boolean }>('/api/auth/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  disableTotp: () => request<{ totpEnabled: boolean }>('/api/auth/totp/disable', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  opportunities: () => request<{ opportunities: Opportunity[]; asOf: number }>('/api/opportunities'),

  /**
   * `minNetPct` (in percent) restricts the window to episodes that clear the
   * bar in force today. Omit it to get the full record, which is what the
   * trade log wants.
   */
  history: (hours: number, minNetPct?: number) =>
    request<{
      since: string;
      minNetPct: number | null;
      episodes: OpportunityEpisode[];
      stats: {
        count: number;
        perHour: number;
        avgNetPct: number;
        bestNetPct: number;
        bySymbol: Array<{ symbol: string; count: number; bestNetPct: number }>;
        byRoute: Array<{ buyExchange: string; sellExchange: string; count: number; bestNetPct: number }>;
      };
    }>(
      `/api/opportunities/history?hours=${hours}` +
        (minNetPct === undefined ? '' : `&minNetPct=${minNetPct}`),
    ),

  status: () =>
    request<{
      feeds: FeedStatus[];
      settings: Settings;
      quoteCurrency: string;
      exchanges: Array<{ id: string; label: string }>;
      routing: VenueRouting[];
      serverTime: number;
    }>('/api/status'),

  balances: (refresh = false) =>
    request<{ balances: VenueBalances[]; cached: boolean; fetchedAt: number }>(
      `/api/balances?refresh=${refresh}`,
    ),

  trades: () =>
    request<{
      trades: Trade[];
      summary: {
        counts: Record<string, number>;
        successfulTrades: number;
        totalNetPnl: number;
        averageNetPnl: number;
        partials: number;
      };
    }>('/api/trades'),

  settings: () => request<{ settings: Settings; exchanges: ExchangeKeyStatus[] }>('/api/settings'),

  updateSettings: (patch: Record<string, unknown>) =>
    request<{ settings: Settings; detectorRestartRequired: boolean }>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  saveKeys: (exchange: string, keys: { apiKey: string; secret: string; password?: string }) =>
    request<{ exchanges: ExchangeKeyStatus[] }>(`/api/exchanges/${exchange}/keys`, {
      method: 'PUT',
      body: JSON.stringify(keys),
    }),

  setExchangeEnabled: (exchange: string, enabled: boolean) =>
    request<{ exchanges: ExchangeKeyStatus[] }>(`/api/exchanges/${exchange}/enabled`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  mode: () => request<ModeState>('/api/mode'),

  pocProgress: () => request<PocProgress>('/api/poc/progress'),

  pocReport: (hours: number, thresholdPct: number) =>
    request<{ hours: number; report: PocReport }>(
      `/api/poc/report?hours=${hours}&thresholdPct=${thresholdPct}`,
    ),

  modeKeys: (mode?: string) =>
    request<{ mode: string; keys: Array<Record<string, unknown>>; guidance: string }>(
      `/api/mode/keys${mode ? `?mode=${mode}` : ''}`,
    ),

  execute: (opportunityId: string, sizeQuote: number) =>
    request<{ ok: boolean }>('/api/execute', {
      method: 'POST',
      body: JSON.stringify({ opportunityId, sizeQuote }),
    }),
};

// ------------------------------------------------------------------ formatting

export const pct = (fraction: number, digits = 3): string =>
  `${fraction > 0 ? '+' : ''}${(fraction * 100).toFixed(digits)}%`;

export const price = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1000) return value.toFixed(2);
  if (value >= 1) return value.toFixed(4);
  return value.toPrecision(6);
};

export const money = (value: number, digits = 2): string =>
  value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const ago = (timestamp: number | null, now = Date.now()): string => {
  if (timestamp === null) return 'never';
  const seconds = (now - timestamp) / 1000;
  if (seconds < 1) return 'now';
  if (seconds < 60) return `${seconds.toFixed(1)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
};

// ------------------------------------------------------------------ execution

export interface TransferExecution {
  id: string;
  opportunityId: string | null;
  symbol: string;
  coin: string;
  buyExchange: string;
  sellExchange: string;
  network: string;
  state: string;
  holdingCoin: boolean;
  sizeQuote: string;
  expectedNetPct: number;
  baseBought: string | null;
  buyAvgPrice: string | null;
  buyOrderId: string | null;
  withdrawalId: string | null;
  amountReceived: string | null;
  sellAvgPrice: string | null;
  quoteReceived: string | null;
  realizedNetPct: number | null;
  netPnl: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface WithdrawalAddress {
  id: number;
  exchange: string;
  coin: string;
  network: string;
  address: string;
  tag: string | null;
  whitelisted: boolean;
  label: string | null;
}

export const executionApi = {
  list: () =>
    request<{
      executions: TransferExecution[];
      holding: TransferExecution[];
      inFlight: Array<{ id: string; state: string }>;
    }>('/api/executions'),

  execute: (opportunityId: string, sizeQuote: number, dryRun: boolean) =>
    request<{ id: string; dryRun: boolean; route: string; coin: string; network: string }>(
      '/api/execute',
      { method: 'POST', body: JSON.stringify({ opportunityId, sizeQuote, dryRun }) },
    ),

  addresses: () => request<{ addresses: WithdrawalAddress[] }>('/api/addresses'),

  saveAddress: (input: Omit<WithdrawalAddress, 'id'>) =>
    request<{ addresses: WithdrawalAddress[] }>('/api/addresses', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};
